import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { ChartCandlestick, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LaunchBadge } from "@/components/LaunchBadge";
import { DEXSCREENER_EMBED_URL, DEXSCREENER_PAIR_URL } from "@/config/dexscreener";
import "@/styles/market-chart.css";
import "@/styles/dexscreener-chart.css";

export const DexScreenerChart = () => {
  const section = useRef(null);
  const timer = useRef(null);
  const nearViewport = useInView(section, { once: true, margin: "400px" });
  const [attempt, setAttempt] = useState(0);
  const [documentState, setDocumentState] = useState("loading");
  const [showRecovery, setShowRecovery] = useState(false);

  useEffect(() => {
    if (!nearViewport) return;
    // A blocked frame can still fire onLoad. Keep recovery independent of it.
    timer.current = setTimeout(() => setShowRecovery(true), 15000);
    return () => clearTimeout(timer.current);
  }, [nearViewport, attempt]);

  const onDocumentLoad = () => {
    // Cross-origin document load is NOT proof the provider's chart rendered.
    // The independent direct link stays available even after this event.
    setDocumentState("loaded");
  };
  const reload = () => { setDocumentState("loading"); setShowRecovery(false); setAttempt((value) => value + 1); };
  const warning = showRecovery || documentState === "error";

  return <section id="chart" ref={section} className="market-chart market-chart--home dex-chart" aria-labelledby="home-dexscreener-heading" data-testid="home-dexscreener-chart">
    <header className="market-chart-heading">
      <div><p className="market-eyebrow" data-testid="home-dexscreener-eyebrow">SOLANA / DEXSCREENER</p><h2 id="home-dexscreener-heading" data-testid="home-dexscreener-heading"><ChartCandlestick aria-hidden="true" /> $KBULL / USD</h2></div>
      <LaunchBadge testId="home-dexscreener-launch-status" />
    </header>
    <div className="market-chart-tool">
      <div className="market-toolbar dex-chart-toolbar">
        <span className="market-source" data-testid="home-dexscreener-source">DEXSCREENER · SOLANA</span>
        <div className="dex-chart-actions">
          <Button asChild variant="ghost"><a href={DEXSCREENER_PAIR_URL} target="_blank" rel="noopener noreferrer" data-testid="home-dexscreener-open">OPEN ON DEXSCREENER <ExternalLink aria-hidden="true" /></a></Button>
          <Button variant="ghost" size="icon" onClick={reload} title="Reload DexScreener chart" aria-label="Reload DexScreener chart" data-testid="home-dexscreener-reload"><RefreshCw aria-hidden="true" /></Button>
        </div>
      </div>
      <div className="dex-chart-frame" data-testid="home-dexscreener-frame-container">
        {nearViewport && <iframe key={attempt} src={DEXSCREENER_EMBED_URL} title="King Bullish / SOL — DexScreener price chart in USD" className="dex-chart-iframe" data-testid="home-dexscreener-iframe" referrerPolicy="strict-origin-when-cross-origin" onLoad={onDocumentLoad} onError={() => { clearTimeout(timer.current); setDocumentState("error"); }} />}
      </div>
    </div>
    <footer className="market-chart-footer">
      <p data-testid="home-dexscreener-launch-notice">Market data is independent of the site's AWAITING LAUNCH status.</p>
      {nearViewport && documentState === "loading" && !warning && <span role="status" data-testid="home-dexscreener-loading">CONNECTING TO DEXSCREENER…</span>}
      {nearViewport && <p role="status" className="dex-chart-warning" data-testid="home-dexscreener-warning">{documentState === "error" ? "DexScreener could not be loaded." : warning ? "Chart blank or blocked?" : "If the embedded chart is unavailable, open it directly."} <a href={DEXSCREENER_PAIR_URL} target="_blank" rel="noopener noreferrer" data-testid="home-dexscreener-fallback">OPEN DEXSCREENER <ExternalLink aria-hidden="true" /></a></p>}
    </footer>
  </section>;
};